/**
 * 📋 SDLC 6.5.0 UNIFIED SPECIFICATION STANDARD FILE HEADER
 * ===============================================================================
 * FILE: frontend/src/hooks/useManagerAttendance.ts
 * PURPOSE: TanStack Query hooks cho màn Chấm công (Quản lý) — Release v0 WP-3 /
 *          SC-011-ATTEND-MGR + ISS-MGR-8 (2026-08-09) date-range query key
 *          + 2026-08-17: BS-400 "hệ thống hiểu ca/khớp ca" redesign — thêm
 *          `employee_ids` vào query key + useResolveAttendance (modal [18]
 *          footer). approve/unapprove/flag/unflag hooks GIỮ NGUYÊN (endpoint
 *          vẫn tồn tại, backward-compat) nhưng KHÔNG còn được gọi từ trang
 *          mới — thay bằng useResolveAttendance theo state machine action_state.
 * SDLC FRAMEWORK VERSION: 6.5.0 (7-Pillar + 9 Mental Models + Long-Running Agent Protocol)
 * DATE: 2026-08-17
 * COMPLIANCE: SDLC 6.5.0 + Sprint Governance Rule 3 (Zero Mock Tolerance)
 * PILLAR: P2 (Sprint Governance) + P4 (Quality Gates)
 * SPRINT GOVERNANCE: Rule 3: Zero-Mock Tolerance | Rule 7: Performance SLAs (<50ms p95)
 *
 * @business-context
 * - Domain: Vietnamese F&B Workforce Management — Chấm công (Manager)
 * - Impact: Revenue Critical — hệ thống hiểu ca/khớp ca tự ghi nhận giờ công
 * - Stakeholders: Branch Managers, Shift Managers
 *
 * @technical-architecture
 * - Layer: Custom Hooks (Business Logic)
 * - Pattern: TanStack Query v5 (useQuery, useMutation, useQueryClient)
 * - Dependencies: @tanstack/react-query, managerApi
 *
 * @cross-references
 * - API Service: frontend/src/services/api/managerApi.ts (getTeamAttendance + resolveAttendance)
 * - API contract: docs/03-integrate/02-api-specifications/SC-011-ATTENDANCE-API-CONTRACT-V1.0.md
 * - Backend: backend/app/api/v2/endpoints/manager_attendance.py
 * - Page: frontend/src/pages/manager/ManagerAttendancePage.tsx
 * - Sibling pattern: frontend/src/hooks/useManagerHub.ts
 * ===============================================================================
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  managerApi,
  type BulkResolveRequest,
  type OTRequestPayload,
  type ResolveRequest,
  type TeamAttendanceParams,
} from "../services/api/managerApi";

export const MANAGER_ATTENDANCE_QUERY_KEYS = {
  all: ["manager", "attendance"] as const,
  // ISS-MGR-8: query key gồm cả date_from/date_to (khoảng ngày) bên cạnh
  // date đơn cũ — 2 chế độ không bao giờ dùng cùng lúc nhưng key phải phân
  // biệt để TanStack Query không cache nhầm giữa 2 khoảng ngày khác nhau.
  // 2026-08-17: + employee_ids ([1b] Tìm nhân viên) — join có thứ tự ổn định
  // (không phụ thuộc thứ tự tick trong panel) để không cache-miss giả.
  list: (params: TeamAttendanceParams) =>
    [
      "manager",
      "attendance",
      params.date ?? "",
      params.date_from ?? "",
      params.date_to ?? "",
      params.work_area_id ?? "",
      params.status ?? "",
      [...(params.employee_ids ?? [])].sort().join(","),
    ] as const,
};

/**
 * Ảnh chấm công (vào/ra) của 1 bản ghi — chỉ fetch khi modal chi tiết mở
 * (enabled theo attendanceId) để không tải ảnh cho cả bảng cùng lúc.
 */
export function useAttendancePhoto(attendanceId: string | null) {
  return useQuery({
    queryKey: ["manager", "attendance", "photo", attendanceId ?? ""],
    queryFn: () => managerApi.getAttendancePhoto(attendanceId as string),
    enabled: !!attendanceId,
    staleTime: 5 * 60_000,
  });
}

/**
 * Bảng [6] chấm công đội theo ngày (+ lọc khu vực / trạng thái server-side).
 * `options.staleTime` cho phép caller override mặc định 30s — dùng khi query
 * này phục vụ dữ liệu PHỤ TRỢ (vd danh sách lựa chọn lọc "Khu vực làm việc",
 * xem `ManagerAttendancePage` — quét khoảng ngày rộng, không cần realtime
 * như bảng chính) thay vì hiển thị trực tiếp lên bảng.
 */
export function useTeamAttendance(params: TeamAttendanceParams, options?: { staleTime?: number }) {
  return useQuery({
    queryKey: MANAGER_ATTENDANCE_QUERY_KEYS.list(params),
    queryFn: () => managerApi.getTeamAttendance(params),
    staleTime: options?.staleTime ?? 30_000,
    retry: 2,
  });
}

// (2026-08-18, gộp #632: #632 dựng lại useAttendancePhoto VĐ4 với contract
// photoType/enabled riêng, trùng tên với bản trên (dòng 59) — bản trên khớp
// backend/frontend contract thật đang giữ (getAttendancePhoto không param),
// xoá bản trùng này để tránh lỗi "Duplicate function implementation".

/** Invalidate mọi query chấm công đội sau mutation (cập nhật dòng theo mockup) */
function useInvalidateAttendance() {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: MANAGER_ATTENDANCE_QUERY_KEYS.all });
}

/** Dialog [11] Duyệt → toast "Đã duyệt chấm công" */
export function useApproveAttendance() {
  const invalidate = useInvalidateAttendance();
  return useMutation({
    mutationFn: ({ attendanceId, reason }: { attendanceId: string; reason: string }) =>
      managerApi.approveAttendance(attendanceId, reason),
    onSuccess: invalidate,
  });
}

/** Menu ⁝ Bỏ duyệt (khi đã duyệt) */
export function useUnapproveAttendance() {
  const invalidate = useInvalidateAttendance();
  return useMutation({
    mutationFn: (attendanceId: string) => managerApi.unapproveAttendance(attendanceId),
    onSuccess: invalidate,
  });
}

/** Bổ sung 2026-08-17 (yêu cầu người dùng — nút "Từ chối" mới): reject với lý do */
export function useRejectAttendance() {
  const invalidate = useInvalidateAttendance();
  return useMutation({
    mutationFn: ({ attendanceId, reason }: { attendanceId: string; reason: string }) =>
      managerApi.rejectAttendance(attendanceId, reason),
    onSuccess: invalidate,
  });
}

/** Popover [10] Gắn cờ → toast "Đã gắn cờ bản ghi" */
export function useFlagAttendance() {
  const invalidate = useInvalidateAttendance();
  return useMutation({
    mutationFn: ({ attendanceId, note }: { attendanceId: string; note: string }) =>
      managerApi.flagAttendance(attendanceId, note),
    onSuccess: invalidate,
  });
}

/** Menu ⁝ Gỡ cờ (khi đã gắn cờ) */
export function useUnflagAttendance() {
  const invalidate = useInvalidateAttendance();
  return useMutation({
    mutationFn: (attendanceId: string) => managerApi.unflagAttendance(attendanceId),
    onSuccess: invalidate,
  });
}

/**
 * Popover [12] Đăng ký / Sửa đăng ký ngoài giờ → toast "Đã đăng ký ngoài giờ".
 * Row đã có attendance → POST (đăng ký) hoặc PUT (sửa); row chưa có
 * (Thiếu chấm / ngày tương lai) → POST by-schedule (upsert plan).
 */
export function useSaveOt() {
  const invalidate = useInvalidateAttendance();
  return useMutation({
    mutationFn: ({
      attendanceId,
      scheduleId,
      isEdit,
      payload,
    }: {
      attendanceId: string | null;
      scheduleId: string;
      isEdit: boolean;
      payload: OTRequestPayload;
    }) => {
      if (attendanceId) {
        return isEdit
          ? managerApi.updateOt(attendanceId, payload)
          : managerApi.registerOt(attendanceId, payload);
      }
      return managerApi.registerOtBySchedule(scheduleId, payload);
    },
    onSuccess: invalidate,
  });
}

/**
 * Modal [18] footer "Ghi nhận công" / "Hoãn ghi nhận" (sau dialog xác nhận
 * [19]) — POST .../resolve. Thay thế approve/unapprove/flag ở trang MỚI.
 */
export function useResolveAttendance() {
  const invalidate = useInvalidateAttendance();
  return useMutation({
    // Dòng "Thiếu chấm công" chưa có bản ghi chấm công nào → định danh bằng ca
    // (`scheduleId`); server tạo bản ghi rồi lưu cùng quyết định.
    mutationFn: ({
      attendanceId,
      scheduleId,
      payload,
    }: {
      attendanceId: string | null;
      scheduleId?: string;
      payload: ResolveRequest;
    }) =>
      attendanceId
        ? managerApi.resolveAttendance(attendanceId, payload)
        : managerApi.resolveAttendanceBySchedule(scheduleId ?? "", payload),
    onSuccess: invalidate,
  });
}

/** Checkbox cột "Thao tác" + thanh bulk "N đã chọn" — 1 lần gọi API cho toàn
 * bộ dòng đã tick, không phải 1 request/dòng. */
export function useBulkResolveAttendance() {
  const invalidate = useInvalidateAttendance();
  return useMutation({
    mutationFn: (payload: BulkResolveRequest) => managerApi.bulkResolveAttendance(payload),
    onSuccess: invalidate,
  });
}
